/*
  SETTE cart drawer
  ---------------------------------------------------------------------------
  Works alongside the theme's own assets/cart-drawer.js (which still opens and
  closes the drawer, and re-renders it after the product form adds to bag).
  This file adds:
    1. The review rotator.
    2. "Continue shopping" close button.
    3. Quantity, Remove and "+ Add" inside the bag. Every change goes through
       Shopify's Cart AJAX API and the drawer is re-rendered by Shopify, so
       every price shown is the real cart price after discounts.
    4. "+ Add" on designs with options opens a small picker in the drawer.
    5. Discount codes: applied to the Shopify cart itself (cart/update.js),
       then shared with Discount Ninja so it can show code pricing on cards.
    6. "Wear It With" pairings from Search & Discovery.
    7. A refresh after add to cart from the custom product hero
       (sections/sette-product-hero-2026q3.liquid), with a loading state so
       she never sees a stale empty bag.
    8. Theme editor support: selecting the section opens the drawer.
*/
(function () {
  if (window.__setteCD) return;
  window.__setteCD = true;

  var SECTION_ID = 'cart-drawer';
  var SOURCE = 'sette-cart-drawer';

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function dnApi() {
    return window.discountNinja && window.discountNinja.api ? window.discountNinja.api : null;
  }

  function refreshDnCardPricing() {
    var api = dnApi();
    if (api && api.strikethroughPricing && api.strikethroughPricing.apply) {
      try { api.strikethroughPricing.apply(); } catch (e) {}
    }
  }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (Number(cents) || 0) / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, key) {
      if (key === 'amount_no_decimals' || key === 'amount_no_decimals_with_comma_separator') {
        return Math.round(amount).toLocaleString('en-US');
      }
      return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    });
  }

  /* ---------------- Reviews rotator ---------------- */

  function initReviews(scope) {
    var root = (scope || document).querySelector('[data-sette-reviews]');
    if (!root || root.__setteInit) return;
    root.__setteInit = true;

    var reviews = root.querySelectorAll('[data-sette-review]');
    var dots = root.querySelectorAll('[data-sette-dot]');
    if (reviews.length < 2) return;

    var seconds = parseFloat(root.getAttribute('data-seconds')) || 3;
    if (seconds < 1) seconds = 1;
    var index = 0;
    var timer = null;
    var holdUntil = 0;

    function show(i) {
      index = (i + reviews.length) % reviews.length;
      for (var r = 0; r < reviews.length; r++) {
        var on = r === index;
        reviews[r].classList.toggle('is-active', on);
        reviews[r].setAttribute('aria-hidden', on ? 'false' : 'true');
      }
      for (var d = 0; d < dots.length; d++) {
        dots[d].classList.toggle('is-active', d === index);
      }
    }

    function tick() {
      if (!document.body.contains(root)) {
        clearInterval(timer);
        return;
      }
      if (Date.now() < holdUntil) return;
      show(index + 1);
    }

    // Steady timer. A finger resting on the band holds the current review for
    // a few seconds; a mouse hovering over it does not stop the rotation.
    root.addEventListener('touchstart', function () {
      holdUntil = Date.now() + 4000;
    }, { passive: true });

    root.__setteShow = function (i) {
      holdUntil = Date.now() + 60000;
      show(i);
    };
    root.__setteResume = function () {
      holdUntil = 0;
    };

    show(0);
    timer = setInterval(tick, seconds * 1000);
  }

  /* ---------------- Sticky footer height ---------------- */

  var footerObserver = null;

  function initFooter(drawer) {
    var bag = drawer.querySelector('[data-sette-bag]');
    var footer = drawer.querySelector('[data-sette-footer]');
    if (footerObserver) {
      footerObserver.disconnect();
      footerObserver = null;
    }
    if (!bag || !footer) return;

    function setHeight() {
      bag.style.setProperty('--sette-cd-footer-h', footer.offsetHeight + 'px');
    }
    setHeight();
    if (window.ResizeObserver) {
      footerObserver = new ResizeObserver(setHeight);
      footerObserver.observe(footer);
    }
  }

  /* ---------------- Wear It With pairings ---------------- */

  var recsCache = {};

  function loadRecs(drawer) {
    var rail = drawer.querySelector('[data-sette-recs]');
    if (!rail || rail.__setteRecs) return;
    rail.__setteRecs = true;

    var productId = rail.getAttribute('data-product-id');
    if (!productId) return;
    var limit = parseInt(rail.getAttribute('data-limit'), 10) || 6;
    var cacheKey = productId + '|' + (rail.getAttribute('data-cart-ids') || '');

    function apply(html) {
      if (!html || !document.body.contains(rail)) return;
      var holder = document.createElement('ul');
      holder.innerHTML = html;
      var cards = holder.querySelectorAll('.sette-cd__card');
      if (cards.length < 2) return;
      rail.innerHTML = '';
      for (var i = 0; i < cards.length && i < limit; i++) {
        rail.appendChild(cards[i]);
      }
      refreshDnCardPricing();
    }

    if (Object.prototype.hasOwnProperty.call(recsCache, cacheKey)) {
      apply(recsCache[cacheKey]);
      return;
    }

    var url = rootUrl() + 'recommendations/products?product_id=' + encodeURIComponent(productId) +
      '&limit=10&intent=complementary&section_id=sette-cart-recs';

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (text) {
        var html = '';
        if (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var result = doc.querySelector('[data-sette-recs-result]');
          html = result ? result.innerHTML : '';
        }
        recsCache[cacheKey] = html;
        apply(html);
      })
      .catch(function () {
        recsCache[cacheKey] = '';
      });
  }

  /* ---------------- Keep the drawer in step ---------------- */

  function syncShell() {
    var drawer = getDrawer();
    if (!drawer) return;
    var panel = drawer.querySelector('.sette-cd__panel');
    drawer.classList.toggle('sette-cd', !!panel);
    if (panel) {
      drawer.classList.toggle('is-empty', !!drawer.querySelector('.sette-cd__empty'));
    }
    initReviews(drawer);
    initFooter(drawer);
    loadRecs(drawer);
  }

  function watch(drawer) {
    if (!drawer || drawer.__setteWatched) return;
    drawer.__setteWatched = true;

    // Contents replaced (by the theme's product form or by this file).
    var inner = drawer.querySelector('#CartDrawer');
    if (inner) {
      new MutationObserver(function () {
        syncShell();
        refreshDnCardPricing();
      }).observe(inner, { childList: true });
    }

    // Drawer opens: measure the footer (it was hidden until now).
    var wasOpen = drawer.classList.contains('active');
    new MutationObserver(function () {
      var open = drawer.classList.contains('active');
      if (open && !wasOpen) {
        initFooter(drawer);
        refreshDnCardPricing();
      }
      wasOpen = open;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    syncShell();
  }

  /* ---------------- Render sections returned by Shopify ---------------- */

  var lastRendered = '';
  var updatingTimer = null;

  function setUpdating(on) {
    var drawer = getDrawer();
    if (!drawer) return;
    clearTimeout(updatingTimer);
    drawer.classList.toggle('is-updating', !!on);
    if (on) {
      updatingTimer = setTimeout(function () { setUpdating(false); }, 5000);
    }
  }

  function renderSections(sections) {
    var drawer = getDrawer();
    if (!drawer || !sections || !sections[SECTION_ID]) return;

    var parser = new DOMParser();
    var doc = parser.parseFromString(sections[SECTION_ID], 'text/html');
    var fresh = doc.querySelector('#CartDrawer');
    var current = drawer.querySelector('#CartDrawer');
    if (fresh && current) {
      var html = fresh.innerHTML;
      // Skip identical re-renders so the reviews and scroll don't reset.
      if (html !== lastRendered || !current.querySelector('.sette-cd__panel')) {
        current.innerHTML = html;
        lastRendered = html;
        var overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer));
        }
      }
    }

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      var bdoc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      var bsec = bdoc.querySelector('.shopify-section');
      if (bsec) bubble.innerHTML = bsec.innerHTML;
    }

    setUpdating(false);
    syncShell();

    if (drawer.classList.contains('active') && typeof window.trapFocus === 'function') {
      var container = drawer.classList.contains('is-empty')
        ? drawer.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      if (container) window.trapFocus(container, drawer.querySelector('.drawer__inner'));
    }
  }

  function announce(cartData) {
    if (typeof window.publish === 'function') {
      try { window.publish('cart-update', { source: SOURCE, cartData: cartData }); } catch (e) {}
    }
  }

  function showError(message) {
    var el = document.querySelector('cart-drawer [data-sette-error]');
    if (!el) return;
    el.textContent = message || 'Something went wrong. Please try again.';
    el.hidden = false;
  }

  function postJSON(path, payload) {
    payload.sections = [SECTION_ID, 'cart-icon-bubble'];
    payload.sections_url = window.location.pathname;
    return fetch(rootUrl() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok || (data && data.status && data.description)) {
          var err = new Error((data && (data.description || data.message)) || 'Cart error');
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  /* ---------------- Quantity, Remove, + Add ---------------- */

  var busy = false;

  function run(path, payload, loadingEl, after) {
    if (busy) return;
    busy = true;
    if (loadingEl) loadingEl.classList.add('is-loading');

    postJSON(path, payload)
      .then(function (data) {
        renderSections(data.sections);
        announce(data);
        if (after) after(data);
      })
      .catch(function (err) {
        if (loadingEl) loadingEl.classList.remove('is-loading');
        showError(err && err.message);
      })
      .finally(function () {
        busy = false;
      });
  }

  function changeLine(key, quantity, lineEl) {
    run('cart/change.js', { id: key, quantity: quantity }, lineEl);
  }

  function addVariant(variantId, button) {
    run('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] }, button);
  }

  /* ---------------- Discount codes ---------------- */

  function currentCodes() {
    var el = document.querySelector('cart-drawer [data-sette-codes]');
    var raw = el ? el.getAttribute('data-sette-codes') || '' : '';
    return raw.split(',').map(function (c) { return c.trim(); }).filter(Boolean);
  }

  function applyCode(code, button) {
    code = (code || '').trim();
    if (!code) return;
    run('cart/update.js', { discount: code }, button, function () {
      var api = dnApi();
      if (api && api.discountCode && api.discountCode.add) {
        try { api.discountCode.add(code); } catch (e) {}
      }
    });
  }

  function removeCode(code, button) {
    var remaining = currentCodes().filter(function (c) {
      return c.toLowerCase() !== String(code).toLowerCase();
    });
    run('cart/update.js', { discount: remaining.join(',') }, button, function () {
      var api = dnApi();
      if (api && api.discountCode && api.discountCode.remove) {
        try { api.discountCode.remove(code); } catch (e) {}
      }
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target && event.target.closest && event.target.closest('cart-drawer [data-sette-code-form]');
    if (!form) return;
    event.preventDefault();
    var input = form.querySelector('input[name="code"]');
    applyCode(input ? input.value : '', form.querySelector('button[type="submit"]'));
  });

  /* ---------------- Options picker ---------------- */

  var productCache = {};

  function getProduct(handle) {
    if (productCache[handle]) return Promise.resolve(productCache[handle]);
    return fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('Product not found');
        return r.json();
      })
      .then(function (p) {
        productCache[handle] = p;
        return p;
      });
  }

  function closeSheet(bag) {
    var scope = bag || document;
    var old = scope.querySelectorAll('.sette-cd__sheet, .sette-cd__sheet-scrim');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function openSheet(bag, product) {
    closeSheet(bag);

    var names = product.options.map(function (o) { return typeof o === 'string' ? o : o.name; });
    var values = product.options.map(function (o, i) {
      if (o && o.values) return o.values;
      var seen = [];
      product.variants.forEach(function (v) {
        if (seen.indexOf(v.options[i]) === -1) seen.push(v.options[i]);
      });
      return seen;
    });
    var firstAvailable = product.variants.filter(function (v) { return v.available; })[0] || product.variants[0];

    // One-value options are set. Size always waits for her choice. Other
    // options (metal) start on the first available combination.
    var selected = names.map(function (name, i) {
      if (values[i].length === 1) return values[i][0];
      if (/size/i.test(name)) return null;
      return firstAvailable ? firstAvailable.options[i] : null;
    });

    function matches(variant, sel) {
      for (var i = 0; i < sel.length; i++) {
        if (sel[i] !== null && variant.options[i] !== sel[i]) return false;
      }
      return true;
    }

    function exactVariant() {
      if (selected.indexOf(null) !== -1) return null;
      return product.variants.filter(function (v) { return matches(v, selected); })[0] || null;
    }

    var scrim = el('div', 'sette-cd__sheet-scrim');
    var sheet = el('div', 'sette-cd__sheet');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-label', 'Choose options for ' + product.title);

    var head = el('div', 'sette-cd__sheet-head');
    var titleWrap = el('div');
    titleWrap.appendChild(el('p', 'sette-cd__sheet-title', product.title));
    var priceEl = el('p', 'sette-cd__sheet-price');
    titleWrap.appendChild(priceEl);
    head.appendChild(titleWrap);
    var closeBtn = el('button', 'sette-cd__sheet-close');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true" focusable="false"><path d="M4 4l12 12M16 4L4 16"/></svg>';
    head.appendChild(closeBtn);
    sheet.appendChild(head);

    var groups = [];
    names.forEach(function (name, i) {
      if (values[i].length < 2) return;
      var group = el('div', 'sette-cd__sheet-opt');
      var label = el('p', 'sette-cd__sheet-label', name);
      var chosen = el('span');
      label.appendChild(chosen);
      group.appendChild(label);
      var chips = el('div', 'sette-cd__chips');
      chips.setAttribute('role', 'group');
      chips.setAttribute('aria-label', name);
      var buttons = values[i].map(function (value) {
        var chip = el('button', 'sette-cd__chip', value);
        chip.type = 'button';
        chip.addEventListener('click', function () {
          selected[i] = value;
          update();
        });
        chips.appendChild(chip);
        return { value: value, node: chip };
      });
      group.appendChild(chips);
      sheet.appendChild(group);
      groups.push({ index: i, chosen: chosen, buttons: buttons });
    });

    var addBtn = el('button', 'sette-cd__checkout sette-cd__sheet-add');
    addBtn.type = 'button';
    sheet.appendChild(addBtn);

    function update() {
      groups.forEach(function (g) {
        g.chosen.textContent = selected[g.index] || '';
        g.buttons.forEach(function (b) {
          var trial = selected.slice();
          trial[g.index] = b.value;
          var possible = product.variants.some(function (v) { return v.available && matches(v, trial); });
          b.node.disabled = !possible;
          b.node.classList.toggle('is-selected', selected[g.index] === b.value);
          b.node.setAttribute('aria-pressed', selected[g.index] === b.value ? 'true' : 'false');
        });
      });

      var variant = exactVariant();
      if (variant) {
        priceEl.textContent = formatMoney(variant.price);
      } else {
        priceEl.textContent = (product.price_varies ? 'From ' : '') + formatMoney(product.price_min || product.price);
      }

      if (variant && variant.available) {
        addBtn.disabled = false;
        addBtn.textContent = 'Add to bag';
      } else if (variant) {
        addBtn.disabled = true;
        addBtn.textContent = 'Sold out';
      } else {
        addBtn.disabled = true;
        var missing = names.filter(function (n, i) { return selected[i] === null; })[0];
        addBtn.textContent = missing ? 'Choose ' + missing.toLowerCase() : 'Choose options';
      }
    }

    addBtn.addEventListener('click', function () {
      var variant = exactVariant();
      if (variant && variant.available) addVariant(variant.id, addBtn);
    });
    closeBtn.addEventListener('click', function () { closeSheet(bag); });
    scrim.addEventListener('click', function () { closeSheet(bag); });

    update();
    bag.appendChild(scrim);
    bag.appendChild(sheet);
    var firstChip = sheet.querySelector('.sette-cd__chip:not(:disabled)');
    (firstChip || addBtn).focus({ preventScroll: true });
  }

  function openPicker(button) {
    var bag = button.closest('[data-sette-bag]');
    var handle = button.getAttribute('data-sette-pick');
    if (!bag || !handle) return;
    button.classList.add('is-loading');
    getProduct(handle)
      .then(function (product) {
        button.classList.remove('is-loading');
        var realOptions = product.variants.length > 1;
        if (!realOptions) {
          var only = product.variants[0];
          if (only && only.available) addVariant(only.id, button);
          return;
        }
        openSheet(bag, product);
      })
      .catch(function () {
        button.classList.remove('is-loading');
        var url = button.getAttribute('data-url');
        if (url) window.location.href = url;
      });
  }

  /* ---------------- Clicks ---------------- */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var closeBtn = target.closest('[data-sette-close]');
    if (closeBtn) {
      event.preventDefault();
      var d = closeBtn.closest('cart-drawer') || getDrawer();
      if (d && typeof d.close === 'function') d.close();
      return;
    }

    var qtyBtn = target.closest('cart-drawer [data-sette-qty]');
    if (qtyBtn) {
      event.preventDefault();
      var qty = parseInt(qtyBtn.getAttribute('data-sette-qty'), 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      changeLine(qtyBtn.getAttribute('data-key'), qty, qtyBtn.closest('[data-sette-line]'));
      return;
    }

    var addBtn = target.closest('cart-drawer [data-sette-add]');
    if (addBtn && addBtn.tagName === 'BUTTON') {
      event.preventDefault();
      addVariant(addBtn.getAttribute('data-sette-add'), addBtn);
      return;
    }

    var pickBtn = target.closest('cart-drawer [data-sette-pick]');
    if (pickBtn) {
      event.preventDefault();
      openPicker(pickBtn);
      return;
    }

    var removeCodeBtn = target.closest('cart-drawer [data-sette-code-remove]');
    if (removeCodeBtn) {
      event.preventDefault();
      removeCode(removeCodeBtn.getAttribute('data-sette-code-remove'), removeCodeBtn);
    }
  });

  /* ---------------- Refresh (product hero adds, DN codes) ---------------- */

  var refreshing = false;
  var refreshAgain = false;

  function requestRefresh(showLoading) {
    if (showLoading) setUpdating(true);
    if (refreshing) {
      refreshAgain = true;
      return;
    }
    refresh();
  }

  function refresh() {
    refreshing = true;
    fetch(rootUrl() + '?sections=' + SECTION_ID + ',cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        renderSections(sections);
      })
      .catch(function (e) {
        setUpdating(false);
        if (window.console) console.error('SETTE cart drawer refresh', e);
      })
      .finally(function () {
        refreshing = false;
        if (refreshAgain) {
          refreshAgain = false;
          refresh();
        }
      });
  }

  // The custom product hero fires these right after it adds to the bag and
  // opens the drawer: show the loading state and fetch the new bag at once.
  document.addEventListener('cart:refresh', function () { requestRefresh(true); });
  document.addEventListener('cart:updated', function () { requestRefresh(false); });

  // A code redeemed through Discount Ninja elsewhere on the site (sticky bar,
  // promo link): apply it to the Shopify cart too so the bag shows it.
  document.addEventListener('la:dn:promotioncode:added', function (event) {
    var d = event && event.detail;
    var code = d && (typeof d === 'string' ? d : (d.code || d.promotionCode || d.discountCode || d.token));
    if (typeof code === 'string' && code && currentCodes().map(function (c) { return c.toLowerCase(); }).indexOf(code.toLowerCase()) === -1) {
      postJSON('cart/update.js', { discount: code }).then(function (data) {
        renderSections(data.sections);
      }).catch(function () {});
    } else {
      requestRefresh(false);
    }
  });
  document.addEventListener('la:dn:promotioncode:removed', function () { requestRefresh(false); });

  // Theme pub/sub. The product form already re-renders the drawer, and this
  // file renders its own changes, so only refresh for anything else.
  if (typeof window.subscribe === 'function') {
    window.subscribe('cart-update', function (event) {
      var source = event && event.source;
      if (source === 'product-form' || source === 'cart-items' || source === SOURCE) return;
      requestRefresh(source === 'sette-hero');
    });
  }

  // The theme's old drawer line items (still defined by the theme) listen for
  // the same event and would load the cart page markup into the drawer.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer-items').then(function () {
      var CDI = customElements.get('cart-drawer-items');
      if (CDI && CDI.prototype) CDI.prototype.onCartUpdate = function () {};
    });
  }

  /* ---------------- Theme editor ---------------- */

  function isThisSection(event) {
    return event && event.target && event.target.id === 'shopify-section-' + SECTION_ID;
  }

  function openInEditor() {
    var drawer = getDrawer();
    if (!drawer) return;
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      drawer.classList.add('animate', 'active');
    }
  }

  function closeInEditor() {
    var drawer = getDrawer();
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (!isThisSection(event)) return;
    lastRendered = '';
    watch(getDrawer());
    openInEditor();
  });
  document.addEventListener('shopify:section:select', function (event) {
    if (isThisSection(event)) openInEditor();
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    if (isThisSection(event)) closeInEditor();
  });
  document.addEventListener('shopify:block:select', function (event) {
    var node = event.target;
    if (!node || !node.hasAttribute || !node.hasAttribute('data-sette-review')) return;
    openInEditor();
    var root = node.closest('[data-sette-reviews]');
    if (!root) return;
    initReviews(root.parentNode);
    var list = root.querySelectorAll('[data-sette-review]');
    for (var i = 0; i < list.length; i++) {
      if (list[i] === node && root.__setteShow) root.__setteShow(i);
    }
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var root = event.target && event.target.closest && event.target.closest('[data-sette-reviews]');
    if (root && root.__setteResume) root.__setteResume();
  });

  /* ---------------- Start ---------------- */

  function start() {
    watch(getDrawer());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
